
import { GoogleGenAI } from "@google/genai";
import { supabase } from '../services/supabaseClient';

// Initialize Gemini Client lazily
let ai: GoogleGenAI | null = null;

const getAiClient = () => {
  if (!ai) {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY || process.env.API_KEY || process.env.GEMINI_API_KEY;
    if (!apiKey) {
      console.warn("API Key for Gemini is missing. AI features will not work.");
      // Return a dummy or throw, but better to throw when used
      throw new Error("Gemini API Key is missing. Please set REACT_APP_GEMINI_API_KEY in .env.local");
    }
    ai = new GoogleGenAI({ apiKey });
  }
  return ai;
};

// PRECIO ESTIMADO GEMINI FLASH (USD por request aprox para simplificar)
const COST_PER_REQUEST = 0.0004;

/**
 * Función auxiliar para registrar el consumo en Supabase
 */
const logApiUsage = async (endpoint: string, model: string, startTime: number, status: 'success' | 'error', errorMsg?: string) => {
  const duration = Date.now() - startTime;
  try {
    await supabase.from('api_logs').insert({
      endpoint,
      model,
      status,
      duration_ms: duration,
      cost_estimated: status === 'success' ? COST_PER_REQUEST : 0,
      error_message: errorMsg || null
    });
  } catch (err) {
    console.error("Error logging API usage:", err); // Silent fail para no romper la app
  }
};

/**
 * Genera una imagen basada en texto usando Gemini 2.5 Flash Image (NanoBanana)
 */
export const generateAIImage = async (prompt: string): Promise<string | null> => {
  const startTime = Date.now();
  const modelName = 'gemini-3.1-flash-image-preview'; // Nano Banana 2

  try {
    const client = getAiClient();
    const response = await client.models.generateContent({
      model: modelName,
      contents: {
        parts: [
          { text: prompt + " 4k resolution, high detail" }
        ],
      },
      config: {
        responseModalities: ['IMAGE'],
        imageConfig: {
          imageSize: '4K',
        }
      },
    });

    const base64Data = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (!base64Data) throw new Error("No se generó imagen");

    // Log Success
    logApiUsage('generate', modelName, startTime, 'success');

    return `data:image/png;base64,${base64Data}`;
  } catch (error: any) {
    // Log Error
    logApiUsage('generate', modelName, startTime, 'error', error.message);
    console.error("Error generando imagen:", error);
    throw error;
  }
};

/**
 * Edita u optimiza una imagen existente basada en una referencia + prompt
 */
export const editAIImage = async (base64Reference: string, prompt: string): Promise<string | null> => {
  const startTime = Date.now();
  const modelName = 'gemini-3.1-flash-image-preview'; // Nano Banana 2

  try {
    const cleanBase64 = base64Reference.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, "");
    const client = getAiClient();

    const response = await client.models.generateContent({
      model: modelName,
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: 'image/png',
              data: cleanBase64
            }
          },
          { text: prompt }
        ],
      },
      config: {
        responseModalities: ['IMAGE'],
        imageConfig: {
          imageSize: '4K',
        }
      },
    });

    const base64Data = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (!base64Data) throw new Error("No se generó imagen");

    // Log Success
    logApiUsage('edit', modelName, startTime, 'success');

    return `data:image/png;base64,${base64Data}`;
  } catch (error: any) {
    // Log Error
    logApiUsage('edit', modelName, startTime, 'error', error.message);
    console.error("Error editando imagen:", error);
    throw error;
  }
};

/**
 * SUPER OPTIMIZADOR WEB (Galería)
 * Enfoca, arregla luz, elimina ruido pero PRIORIZA el peso mínimo.
 */
export const optimizeWebImage = async (base64Reference: string): Promise<string | null> => {
  const optimizationPrompt = `
    Super Web Optimizer Task:
    1. EXTREME COMPRESSION GOAL: Create the most efficient file size possible while maintaining web readability.
    2. TECHNICAL FIX: Sharpen focus perfectly, fix lighting defects, remove noise/grain.
    3. AESTHETIC: Ensure professional studio lighting look.
    4. STRICT: Do NOT change the composition, subject, or background elements. Only enhance quality and reduce data complexity.
    5. Output: High-definition look but visually optimized for minimum bandwidth.
  `;
  return await editAIImage(base64Reference, optimizationPrompt);
};

/**
 * Optimiza el prompt ingresado por el usuario usando un modelo de lenguaje.
 */
export const improveAIPrompt = async (prompt: string, context?: string): Promise<string> => {
  const startTime = Date.now();
  // Para prompts de texto puro, usamos gemini-2.5-flash (sin "-image") 
  // O podemos usar flash también para texto si la API lo permite, 
  // pero generalmente flash o pro funcionan mejor para texto.
  const modelName = 'gemini-2.5-flash';

  try {
    const client = getAiClient();
    
    let instructions = `You are an expert prompt engineer for AI image generators (like Midjourney, Stable Diffusion, and Gemini Image).
Your task is to take the user's basic idea and turn it into a highly detailed, descriptive, and visually rich prompt string in English.
Provide ONLY the final improved prompt text. No explanations. No intros.

User's idea: "${prompt}"`;

    if (context) {
      instructions += `\n\nContext to include: ${context}`;
    }

    const response = await client.models.generateContent({
      model: modelName,
      contents: [
        { role: 'user', parts: [{ text: instructions }] }
      ],
    });

    const refinedPrompt = response.text || response.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!refinedPrompt) throw new Error("No se pudo mejorar el prompt");

    logApiUsage('improve_prompt', modelName, startTime, 'success');
    return refinedPrompt;
  } catch (error: any) {
    logApiUsage('improve_prompt', modelName, startTime, 'error', error.message);
    console.error("Error mejorando prompt:", error);
    throw error;
  }
};
